import React, { useEffect, useRef, useState } from 'react';
import { Check, Sparkles, UserRound } from 'lucide-react';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { Eyebrow } from './VitrineUi';
import type { ExempleConseillere } from './vitrineConseillere';

/** La Conseillère IA en situation : une conversation réaliste (un onglet par métier s'il y en a plusieurs), et ce que le professionnel garde en main. */
export const ConseillereExemple: React.FC<{ exemples: ExempleConseillere[] }> = ({ exemples }) => {
    const { tokens } = useAmbiance();
    const [k, setK] = useState(0);
    const exemple = exemples[Math.min(k, exemples.length - 1)];
    const total = exemple.echanges.length;
    const [vus, setVus] = useState(total);
    const [saisie, setSaisie] = useState(false);
    const [enVue, setEnVue] = useState(false);
    const cadre = useRef<HTMLDivElement>(null);
    const fil = useRef<HTMLDivElement>(null);
    const sansAnimation = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Le rejeu ne tourne que lorsque la conversation est à l'écran.
    useEffect(() => {
        const el = cadre.current;
        if (!el || typeof IntersectionObserver === 'undefined') { setEnVue(true); return; }
        const obs = new IntersectionObserver(([entree]) => setEnVue(entree.isIntersecting), { threshold: 0.35 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (sansAnimation || !enVue) { setVus(total); setSaisie(false); return; }
        let arret = false;
        let minuteur: ReturnType<typeof setTimeout>;
        const attendre = (ms: number) => new Promise<void>((ok) => { minuteur = setTimeout(ok, ms); });
        (async () => {
            while (!arret) {
                setVus(0);
                await attendre(700);
                for (let i = 0; i < total && !arret; i++) {
                    const e = exemple.echanges[i];
                    if (e.de === 'ia') { setSaisie(true); await attendre(900); if (arret) return; setSaisie(false); }
                    else await attendre(Math.min(2200, 600 + e.texte.length * 22));
                    if (arret) return;
                    setVus(i + 1);
                }
                await attendre(4500);
            }
        })();
        return () => { arret = true; clearTimeout(minuteur); setSaisie(false); };
    }, [enVue, k, total, sansAnimation]);

    useEffect(() => { if (fil.current) fil.current.scrollTop = fil.current.scrollHeight; }, [vus, saisie]);
    const resume = 'Exemple de conversation avec la Conseillère IA : ' + exemple.echanges.map((e) => e.texte).join(' ');
    return (
        <section id="conseillere-ia" className="bg-surface-app border-y border-border scroll-mt-40">
            <div className="max-w-[1160px] mx-auto px-5 py-14 md:py-20">
                {exemples.length > 1 && (
                    <div className="flex flex-wrap items-center gap-2 mb-8" role="tablist" aria-label="Choisir un métier">
                        <span className="text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mr-2">Exemple</span>
                        {exemples.map((x, i) => (
                            <button key={x.label} type="button" role="tab" aria-selected={i === k} onClick={() => setK(i)} className={'rounded-full border px-4 py-2 text-[13.5px] font-semibold transition-colors ' + (i === k ? 'bg-content text-surface border-content' : 'bg-surface border-border hover:border-content')}>{x.label}</button>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div>
                        <Eyebrow>Conseillère IA</Eyebrow>
                        <h2 className="text-[28px] md:text-[38px] font-semibold leading-[1.08] tracking-[-0.02em] mb-4">{exemple.titre}</h2>
                        <p className="text-[15.5px] text-content-muted leading-relaxed mb-5">{exemple.intro}</p>
                        <ul className="space-y-2.5">
                            {exemple.coulisses.map((t) => (
                                <li key={t} className="flex gap-2.5 text-[14.5px] leading-snug"><Check size={16} className="mt-0.5 shrink-0" style={{ color: tokens.acc }} aria-hidden="true" />{t}</li>
                            ))}
                        </ul>
                        <p className="mt-5 text-[12.5px] text-content-muted">Incluse dans la formule Croître.</p>
                    </div>
                    <div ref={cadre} className="rounded-2xl border border-border bg-surface shadow-[0_24px_60px_-20px_rgba(0,0,0,.25)] overflow-hidden" role="img" aria-label={resume}>
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-surface-soft">
                            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: tokens.acc }}><Sparkles size={16} aria-hidden="true" /></span>
                            <div className="min-w-0"><div className="text-[14px] font-semibold leading-none">Conseillère</div><div className="text-[11.5px] text-content-muted mt-1 truncate">{exemple.contexte}</div></div>
                        </div>
                        <div ref={fil} className="p-5 space-y-3 h-[440px] overflow-y-auto scroll-smooth" aria-hidden="true">
                            {exemple.echanges.slice(0, vus).map((e, i) => e.de === 'equipe' ? (
                                <div key={i} className="flex items-center justify-center gap-2 pt-1 text-[12px] font-semibold text-content-muted"><UserRound size={14} />{e.texte}</div>
                            ) : (
                                <div key={i} className={'max-w-[84%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-snug ' + (e.de === 'client' ? 'bg-surface-soft border border-border rounded-bl-sm' : 'ml-auto text-white rounded-br-sm')} style={e.de === 'ia' ? { background: tokens.acc } : undefined}>{e.texte}</div>
                            ))}
                            {saisie && <div className="max-w-[84%] rounded-2xl rounded-br-sm px-4 py-3 ml-auto text-white inline-flex gap-1" style={{ background: tokens.acc }}><span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" /><span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse [animation-delay:150ms]" /><span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse [animation-delay:300ms]" /></div>}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
